<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Table from '@/Components/Table/Table.vue'
import Tag from '@/Components/Utils/Tag.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import CreateOrderModal from '@/Components/Orders/CreateOrderModal.vue'

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {}
    seng_products: { id: number; name: string; code: string; is_pinned: boolean; price: number }[]
    customers: { id: number; name: string; orders_count: number }[]
    flexo_product: { id: number; name: string; price: number } | null
    flexo_customer_prices: Record<number, number>
    default_product_id: number | null
}>()

const isCreateModalOpen = ref(false)
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #button-create-order="{ action }">
            <Button
                @click="isCreateModalOpen = true"
                :style="action.style"
                :label="action.label"
                :icon="action.icon"
                :tooltip="action.tooltip"
            />
        </template>
    </PageHeading>
    <Table :resource="data" name="default">
        <template #cell(created_at)="{ item }">
            {{ useFormatTime(item.created_at) }}
        </template>

        <template #cell(reference)="{ item }">
            <div class="flex items-center gap-x-2">
                <Link :href="route('casheer.orders.show', item.slug)" class="primaryLink">
                    {{ item.reference }}
                </Link>
                
                <Icon v-if="item.manufacture_state_icon" :data="item.manufacture_state_icon" />
            </div>
            <div class="text-sm italic opacity-60">
                {{ item.file_merek }}
            </div>
        </template>

        <template #cell(customer)="{ item }">
            {{ item.customer?.name ?? '-' }}
        </template>

        <template #cell(type)="{ item }">
            <div class="flex items-center gap-x-1.5">
                <Icon :data="item.type_icon" />
                <span>{{ item.type_icon?.tooltip }}</span>
            </div>
        </template>

        <template #cell(billing_status)="{ item }">
            <Tag
                :label="item.billing_status_label"
                :stringToColor="false"
                :theme="item.billing_status === 'paid' ? 3 : 7"
            />
        </template>

        <template #cell(total_amount)="{ item }">
            {{ useFormatCurrency(item.total_amount) }}
        </template>
    </Table>

    <CreateOrderModal
        v-model:visible="isCreateModalOpen"
        :seng_products="seng_products"
        :customers="customers"
        :flexo_product="flexo_product"
        :flexo_customer_prices="flexo_customer_prices"
        :default_product_id="default_product_id"
    />
</template>
